/**
 * Find a Clinic — Leaflet map (no API key/account required) with Esri's
 * "Light Gray Canvas" tiles for the muted, desaturated-gray look, custom
 * teal pins, and a click-triggered popup card matching the location cards
 * used on the Home page / footer (same underlying data — see
 * page-find-a-clinic.php).
 *
 * Originally built against the Google Maps JS API, then against CartoDB
 * Positron tiles — both switched away from: Google needs a billed Cloud
 * project, and CARTO's basemaps.cartocdn.com now stamps an "API KEY
 * REQUIRED" watermark on unauthenticated tiles (a policy change after this
 * was first written — verified live, not assumed). Esri's ArcGIS Online
 * "Canvas" tile services are still genuinely free/keyless for this kind of
 * use, so the map is built on two stacked layers from that service:
 * World_Light_Gray_Base (the gray canvas itself) and
 * World_Light_Gray_Reference (labels/roads on top) — CARTO's single-layer
 * style bundled both into one image; Esri ships them separately.
 *
 * Its own webpack entry (assets/js/find-a-clinic-map.js), not bundled into
 * main.js — Leaflet is ~34KB that only the Find a Clinic page needs.
 * Enqueued only on that page template, see functions.php. Self-invokes at
 * the bottom since nothing else imports/calls this as a module anymore.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Esri "Light Gray Canvas" — free, no API key, no account. Tile URLs use
// ArcGIS's {z}/{y}/{x} ordering (row before column), not the {x}/{y} most
// XYZ tile services use — verified against a live tile fetch, not assumed.
const BASE_TILE_URL =
    'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
const REFERENCE_TILE_URL =
    'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}';
const TILE_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, ' +
    'Esri, HERE, Garmin, FAO, NOAA, USGS';

function createPopupElement( loc, icons ) {
    const el = document.createElement( 'div' );
    el.className = 'find-a-clinic-popup';

    const detailsLink = loc.detailsUrl
        ? `<a class="find-a-clinic-popup__details-link" href="${ loc.detailsUrl }">${ loc.detailsLabel }<span class="link-arrow" aria-hidden="true"></span></a>`
        : '';

    const phoneHtml = loc.phoneUrl
        ? `<a class="phone-link find-a-clinic-popup__phone" href="${ loc.phoneUrl }">${ loc.phone }</a>`
        : `<span class="find-a-clinic-popup__phone">${ loc.phone }</span>`;

    const phoneRow = loc.phone
        ? `<div class="find-a-clinic-popup__row find-a-clinic-popup__row--center">
                <img class="find-a-clinic-popup__icon" src="${ icons.phone }" width="16" height="16" alt="" aria-hidden="true">
                ${ phoneHtml }
           </div>`
        : '';

    const hoursRow = loc.hours
        ? `<div class="find-a-clinic-popup__row">
                <img class="find-a-clinic-popup__icon" src="${ icons.hours }" width="15" height="19" alt="" aria-hidden="true">
                <div class="find-a-clinic-popup__hours">${ loc.hours }</div>
           </div>`
        : '';

    const extendedRow = loc.extendedHours
        ? `<div class="find-a-clinic-popup__row">
                <img class="find-a-clinic-popup__icon" src="${ icons.extended }" width="18" height="18" alt="" aria-hidden="true">
                <div class="find-a-clinic-popup__extended-hours"><strong>Extended Hours:</strong> ${ loc.extendedHours }</div>
           </div>`
        : '';

    const addressHtml = loc.mapsUrl
        ? `<a class="address-link" href="${ loc.mapsUrl }" target="_blank" rel="noopener">${ loc.address }<span class="uk-hidden-visually"> (opens in Google Maps)</span></a>`
        : loc.address;

    const scheduleLink = loc.scheduleUrl
        ? `<a class="find-a-clinic-popup__schedule-link" href="${ loc.scheduleUrl }">${ loc.scheduleLabel }<span class="link-arrow" aria-hidden="true"></span></a>`
        : '';

    el.innerHTML = `
        <h3 class="find-a-clinic-popup__name">${ loc.name }</h3>
        ${ detailsLink }
        <div class="find-a-clinic-popup__row">
            <img class="find-a-clinic-popup__icon" src="${ icons.address }" width="16" height="16" alt="" aria-hidden="true">
            <div class="find-a-clinic-popup__address">${ addressHtml }</div>
        </div>
        ${ phoneRow }
        ${ hoursRow }
        ${ extendedRow }
        ${ scheduleLink }
    `;

    return el;
}

function initFindAClinicMap() {
    const canvas = document.getElementById( 'find-a-clinic-map-canvas' );
    if ( ! canvas ) return;

    let locations = [];
    try {
        locations = JSON.parse( canvas.dataset.locations || '[]' );
    } catch ( e ) {
        return;
    }

    if ( ! locations.length ) return;

    const icons = {
        marker:   canvas.dataset.markerIcon,
        address:  canvas.dataset.iconAddress,
        phone:    canvas.dataset.iconPhone,
        hours:    canvas.dataset.iconHours,
        extended: canvas.dataset.iconExtended,
    };

    const pinIcon = L.icon( {
        iconUrl:     icons.marker,
        iconSize:    [ 36, 44 ],
        iconAnchor:  [ 18, 44 ], // tip of the pin points at the coordinate
        popupAnchor: [ 0, -44 ], // popup opens above the pin's tip, not its center
    } );

    // Centered on Tualatin (the middle location geographically) rather than
    // computed from fitBounds() — a fixed view of all three clinics rather
    // than one that tightens/shifts as locations are added or edited. The
    // small latitude bump shifts the visible window north (map view "moves
    // up") so Portland — the northernmost pin — isn't cut off at the top.
    const tualatin = locations.find( ( loc ) => loc.name === 'Tualatin' ) || locations[ 0 ];

    // The map canvas gets narrow enough in this specific width range (small
    // tablet/large phone) that zoom 11 crops a pin at the card's default
    // size — one step out gives the same view more breathing room without
    // affecting any other breakpoint. Mobile phones ($breakpoint-small,
    // 640px) get a second step out on top of that, same reasoning.
    const isNarrowTablet = window.matchMedia( '(min-width: 712px) and (max-width: 820px)' ).matches;
    const isMobile       = window.matchMedia( '(max-width: 640px)' ).matches;

    // Touch screens: one-finger drag panned the map, so a swipe that started
    // on it could never scroll the page past it. With dragging off, Leaflet
    // drops its leaflet-touch-drag class and leaflet.css falls back to
    // touch-action: pan-x pan-y — one finger scrolls the page, two-finger
    // pinch still zooms the map, and the pins/+/- controls still work.
    const isTouch = window.matchMedia( '(pointer: coarse)' ).matches;

    const map = L.map( canvas, {
        center:          [ tualatin.lat + 0.05, tualatin.lng ],
        zoom:            isMobile ? 9 : ( isNarrowTablet ? 10 : 11 ),
        scrollWheelZoom: false, // requires a click first — avoids trapping page-scroll under the cursor
        dragging:        ! isTouch,
        zoomControl:     true,
    } );

    // Esri's Light Gray Canvas service tops out at level 16 — tiles beyond
    // that are blank/stretched, unlike CARTO's/Google's higher ceilings.
    L.tileLayer( BASE_TILE_URL, {
        attribution: TILE_ATTRIBUTION,
        maxZoom:     16,
    } ).addTo( map );

    L.tileLayer( REFERENCE_TILE_URL, {
        maxZoom: 16,
    } ).addTo( map );

    map.on( 'click', () => map.scrollWheelZoom.enable() );

    locations.forEach( ( loc ) => {
        L.marker( [ loc.lat, loc.lng ], { icon: pinIcon, title: loc.name } )
            .addTo( map )
            .bindPopup( () => createPopupElement( loc, icons ), { maxWidth: 360, className: 'find-a-clinic-popup-wrapper' } );
    } );
}

// Script is footer-loaded so the DOM is already ready at this point
// (see functions.php — same defer/in_footer strategy as the main bundle).
initFindAClinicMap();
